// ═══════════════════════════════════════════════════════════════
// Handoff — Action Items
// Full CRUD with filters, priorities, deadlines, live countdowns
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Modal, Textarea,
} from '@mantine/core';
import { IconPlus, IconSearch, IconClock } from '@tabler/icons-react';
import { useHandoffStore } from '../../store/handoffStore';
import { useAuthStore } from '../../store/authStore';
import { PRIORITY_META } from '../../types';
import type { ActionPriority } from '../../types';
import { secondary } from '../../lib/colors';

function timeUntil(deadline: string): { text: string; overdue: boolean } {
  const now = Date.now();
  const dl = new Date(deadline).getTime();
  const diff = dl - now;
  if (diff < 0) return { text: 'OVERDUE', overdue: true };
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  if (days > 0) return { text: `${days}d ${hours}h`, overdue: false };
  return { text: `${hours}h`, overdue: false };
}

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Active/Pending' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
];

export default function ActionItems() {
  const { actionItems, rosterUsers, loadActionItems, loadRosterUsers, createActionItem, completeActionItem, reopenActionItem, deleteActionItem } = useHandoffStore();
  const { incidents, loadIncidents } = useAuthStore();
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('pending');
  const [eventFilter, setEventFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [priority, setPriority] = useState<ActionPriority>('normal');
  const [assignee, setAssignee] = useState('');
  const [deadline, setDeadline] = useState('');
  const [eventId, setEventId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadActionItems(); loadIncidents(); loadRosterUsers(); }, []);

  const eventOptions = [
    { value: 'all', label: 'All Incidents' },
    ...incidents.filter(e => e.id != null).map(e => ({ value: String(e.id), label: e.name })),
  ];

  const now = Date.now();
  const filtered = actionItems
    .filter(a => {
      if (filter === 'pending') return a.status === 'pending';
      if (filter === 'overdue') return a.status === 'pending' && a.deadline && new Date(a.deadline).getTime() < now;
      if (filter === 'completed') return a.status === 'completed';
      return true;
    })
    .filter(a => eventFilter === 'all' || String(a.eventId) === eventFilter)
    .filter(a => !search || a.title.toLowerCase().includes(search.toLowerCase()) || a.assignedToName.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
      const pOrder: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };
      return (pOrder[a.priority] ?? 2) - (pOrder[b.priority] ?? 2);
    });

  const handleCreate = async () => {
    if (!title.trim() || !eventId) return;
    setSaving(true);
    await createActionItem({
      eventId, title: title.trim(), description: desc.trim(),
      priority, assignedToName: assignee, deadline,
    });
    setCreateOpen(false);
    setTitle(''); setDesc(''); setPriority('normal'); setAssignee(''); setDeadline(''); setEventId('');
    setSaving(false);
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Action Items</Title>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setCreateOpen(true)}>New Action</Button>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search..." leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.currentTarget.value)} size="xs" w={200} />
        <Select data={FILTER_OPTIONS} value={filter} onChange={v => setFilter(v ?? 'pending')} size="xs" w={160} />
        <Select data={eventOptions} value={eventFilter} onChange={v => setEventFilter(v ?? 'all')} size="xs" w={220} />
      </Group>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No action items found.</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map(a => {
            const evt = incidents.find(e => String(e.id) === String(a.eventId));
            const pm = PRIORITY_META[a.priority];
            const isOverdue = a.status === 'pending' && a.deadline && new Date(a.deadline).getTime() < now;
            const countdown = a.deadline ? timeUntil(a.deadline) : null;
            return (
              <Paper key={a.id} p="sm" radius="sm" withBorder
                style={{ borderLeft: `3px solid ${isOverdue ? 'var(--color-error)' : pm.color}`, opacity: a.status === 'completed' ? 0.6 : 1 }}>
                <Group justify="space-between" wrap="nowrap">
                  <div style={{ flex: 1 }}>
                    <Group gap="xs" mb={2}>
                      <Text size="sm" fw={500} td={a.status === 'completed' ? 'line-through' : undefined}>{a.title}</Text>
                      <Badge size="xs" style={{ background: pm.color + '15', color: pm.color }}>{pm.label}</Badge>
                      {isOverdue && <Badge size="xs" color="red">Overdue</Badge>}
                      {a.status === 'completed' && <Badge size="xs" color="green">Done</Badge>}
                    </Group>
                    {a.description && <Text size="xs" c="dimmed" lineClamp={1} mb={2}>{a.description}</Text>}
                    <Group gap="md">
                      <Text size="xs" c="dimmed">{evt?.name}</Text>
                      {a.assignedToName && <Text size="xs" c="dimmed">Assigned: {a.assignedToName}</Text>}
                      {countdown && (
                        <Group gap={4}>
                          <IconClock size={10} color={countdown.overdue ? 'var(--color-error)' : secondary[700]} />
                          <Text size="xs" c={countdown.overdue ? 'var(--color-error)' : 'dimmed'} fw={countdown.overdue ? 600 : 400}>
                            {countdown.text}
                          </Text>
                        </Group>
                      )}
                      {a.status === 'completed' && a.completedByName && (
                        <Text size="xs" c="dimmed">Completed by {a.completedByName}</Text>
                      )}
                    </Group>
                  </div>
                  <Group gap="xs">
                    {a.status === 'pending' ? (
                      <Button variant="subtle" size="xs" color="green" onClick={() => completeActionItem(a.id)}>
                        Complete
                      </Button>
                    ) : (
                      <Button variant="subtle" size="xs" onClick={() => reopenActionItem(a.id)}>
                        Reopen
                      </Button>
                    )}
                    <Button variant="subtle" size="xs" color="red" onClick={() => {
                      if (confirm('Delete this action item?')) deleteActionItem(a.id);
                    }}>Delete</Button>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}

      {/* Create Modal */}
      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="New Action Item" size="md">
        <Stack gap="sm">
          <Select label="Event" placeholder="Select event"
            data={incidents.filter(e => e.id != null).map(e => ({ value: String(e.id), label: e.name }))}
            value={eventId} onChange={v => setEventId(v ?? '')} required />
          <TextInput label="Title" value={title} onChange={e => setTitle(e.currentTarget.value)} required />
          <Textarea label="Description" value={desc} onChange={e => setDesc(e.currentTarget.value)} minRows={2} />
          <Group grow>
            <Select label="Priority"
              data={Object.entries(PRIORITY_META).map(([v, m]) => ({ value: v, label: m.label }))}
              value={priority} onChange={v => setPriority((v as ActionPriority) ?? 'normal')} />
            <Select label="Assignee" placeholder="Select person..."
              data={rosterUsers.map(u => ({ value: u.fullName, label: u.fullName }))}
              value={assignee || null} onChange={v => setAssignee(v ?? '')}
              searchable clearable />
          </Group>
          <TextInput label="Deadline" type="date" value={deadline}
            onChange={e => setDeadline(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} loading={saving} disabled={!title.trim() || !eventId}>Create</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
